import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { 
  ArrowLeft, PieChart, Info, CheckCircle, AlertTriangle, TrendingUp, 
  Home, ShoppingBag, PiggyBank, Download, Wallet, CreditCard,
  Zap, Droplets, Wifi, Car, ShoppingCart, Utensils, Tv, Shirt, Plane, 
  DumbbellIcon, Shield, Building
} from "lucide-react";
import {
  PieChart as RechartsPieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
} from "recharts";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";

// Currency formatter
const fmt = (n) =>
  new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP", maximumFractionDigits: 0 }).format(n);

const fmtDecimal = (n) =>
  new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP", minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);

// Colors
const COLORS = {
  needs: "#0ea5e9",
  wants: "#a855f7", 
  savings: "#10b981",
  debt: "#f97316",
  income: "#22c55e",
};

// Pay frequency multipliers to monthly
const PAY_FREQUENCY = {
  weekly: { label: "Weekly", multiplier: 52 / 12 },
  fortnightly: { label: "Fortnightly", multiplier: 26 / 12 },
  monthly: { label: "Monthly", multiplier: 1 },
  annually: { label: "Annually", multiplier: 1 / 12 },
};

// Essential Expenses (Needs)
const ESSENTIAL_EXPENSES = [
  { key: "rentMortgage", label: "Rent / Mortgage", icon: Building, placeholder: "1200" },
  { key: "councilTax", label: "Council Tax", icon: Home, placeholder: "150" },
  { key: "gasElectric", label: "Gas & Electric", icon: Zap, placeholder: "120" },
  { key: "water", label: "Water", icon: Droplets, placeholder: "35" },
  { key: "broadbandMobile", label: "Broadband & Mobile", icon: Wifi, placeholder: "60" },
  { key: "transport", label: "Transport & Car Insurance", icon: Car, placeholder: "200" },
  { key: "groceries", label: "Groceries", icon: ShoppingCart, placeholder: "350" },
  { key: "insurance", label: "Life & Health Insurance", icon: Shield, placeholder: "50" },
];

// Discretionary Spending (Wants)
const DISCRETIONARY_EXPENSES = [
  { key: "eatingOut", label: "Eating Out & Takeaways", icon: Utensils, placeholder: "150" },
  { key: "entertainment", label: "Entertainment & Hobbies", icon: Tv, placeholder: "80" },
  { key: "shopping", label: "Shopping & Clothes", icon: Shirt, placeholder: "100" },
  { key: "travel", label: "Travel & Holidays", icon: Plane, placeholder: "100" },
  { key: "subscriptions", label: "Subscriptions (Netflix, Gym)", icon: DumbbellIcon, placeholder: "50" },
];

// Custom Tooltip for Pie Chart
const CustomTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    return (
      <div className="bg-slate-900 border border-slate-700 rounded-xl px-4 py-3 shadow-xl">
        <p className="text-white font-semibold">{data.name}</p>
        <p className="text-slate-400 text-sm">{fmt(data.value)} ({data.percent}%)</p>
      </div>
    );
  }
  return null;
};

// Input Field Component
function InputField({ label, icon: Icon, value, onChange, placeholder, prefix = "£" }) {
  return (
    <div className="space-y-1.5">
      <label className="flex items-center gap-2 text-slate-400 text-sm font-medium">
        {Icon && <Icon className="w-4 h-4 text-slate-500" />}
        {label}
      </label>
      <div className="relative">
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 text-sm">{prefix}</span>
        <input
          type="number"
          value={value || ""}
          onChange={(e) => onChange(Math.max(0, Number(e.target.value) || 0))}
          placeholder={placeholder}
          className="w-full bg-slate-950 border border-slate-700 focus:border-emerald-500/60 focus:ring-1 focus:ring-emerald-500/20 rounded-lg pl-8 pr-4 py-2.5 text-white text-sm outline-none transition-colors placeholder:text-slate-600"
        />
      </div>
    </div>
  );
}

// Section Header Component
function SectionHeader({ icon: Icon, title, subtitle, color = "text-emerald-400" }) {
  return (
    <div className="flex items-start gap-3 mb-4">
      <div className={`w-10 h-10 rounded-xl bg-slate-800 flex items-center justify-center flex-shrink-0`}>
        <Icon className={`w-5 h-5 ${color}`} />
      </div>
      <div>
        <h3 className="font-outfit font-semibold text-white text-lg">{title}</h3>
        {subtitle && <p className="text-slate-500 text-sm">{subtitle}</p>}
      </div>
    </div>
  );
}

// Health Score Component
function HealthScore({ score }) {
  const getScoreColor = () => {
    if (score >= 80) return "text-emerald-400";
    if (score >= 60) return "text-green-400";
    if (score >= 40) return "text-amber-400";
    return "text-red-400";
  };
  
  const getScoreLabel = () => {
    if (score >= 80) return "Excellent";
    if (score >= 60) return "Good";
    if (score >= 40) return "Fair";
    return "Needs Work";
  };

  return (
    <div className="text-center">
      <div className={`text-5xl font-bold ${getScoreColor()}`}>{score}</div>
      <div className="text-slate-500 text-sm mt-1">{getScoreLabel()}</div>
    </div>
  );
}

export default function BudgetCalculator() {
  // Income State
  const [payFrequency, setPayFrequency] = useState("monthly");
  const [netSalary, setNetSalary] = useState(2500);
  const [partnerSalary, setPartnerSalary] = useState(0);
  const [annualBonus, setAnnualBonus] = useState(0);

  // Essential Expenses State
  const [essentials, setEssentials] = useState({
    rentMortgage: 1200,
    councilTax: 150,
    gasElectric: 120,
    water: 35,
    broadbandMobile: 60,
    transport: 200,
    groceries: 350,
    insurance: 50,
  });

  // Discretionary Expenses State
  const [discretionary, setDiscretionary] = useState({
    eatingOut: 150,
    entertainment: 80,
    shopping: 100,
    travel: 100,
    subscriptions: 50,
  });

  // Debts State
  const [debts, setDebts] = useState({
    creditCards: 0,
    loans: 0,
    studentLoan: 0,
    other: 0,
  });

  // Savings State
  const [savings, setSavings] = useState({
    emergencyFund: 500,
    monthlyContribution: 200,
    pensionTopUp: 0,
    investments: 0,
  });

  // Calculations
  const calculations = useMemo(() => {
    // Monthly income
    const multiplier = PAY_FREQUENCY[payFrequency].multiplier;
    const monthlyMainSalary = netSalary * multiplier;
    const monthlyPartnerSalary = partnerSalary * multiplier;
    const monthlyBonus = annualBonus / 12;
    const totalMonthlyIncome = monthlyMainSalary + monthlyPartnerSalary + monthlyBonus;

    // Total essentials (needs)
    const totalEssentials = Object.values(essentials).reduce((a, b) => a + b, 0);
    
    // Total discretionary (wants)
    const totalDiscretionary = Object.values(discretionary).reduce((a, b) => a + b, 0);
    
    // Total debts
    const totalDebts = Object.values(debts).reduce((a, b) => a + b, 0);
    
    // Total savings
    const totalSavings = savings.monthlyContribution + savings.pensionTopUp + savings.investments;
    
    // Total expenses
    const totalExpenses = totalEssentials + totalDiscretionary + totalDebts + totalSavings;
    
    // Surplus/Deficit
    const surplus = totalMonthlyIncome - totalExpenses;
    
    // Percentages
    const needsPercent = totalMonthlyIncome > 0 ? (totalEssentials / totalMonthlyIncome) * 100 : 0;
    const wantsPercent = totalMonthlyIncome > 0 ? (totalDiscretionary / totalMonthlyIncome) * 100 : 0;
    const savingsPercent = totalMonthlyIncome > 0 ? (totalSavings / totalMonthlyIncome) * 100 : 0;
    const debtPercent = totalMonthlyIncome > 0 ? (totalDebts / totalMonthlyIncome) * 100 : 0;
    
    // Emergency fund months covered
    const monthlyEssentialCosts = totalEssentials;
    const emergencyMonths = monthlyEssentialCosts > 0 ? savings.emergencyFund / monthlyEssentialCosts : 0;
    
    // Health Score Calculation (0-100)
    let healthScore = 50; // Base score
    
    // Savings rate bonus (max +25)
    if (savingsPercent >= 20) healthScore += 25;
    else if (savingsPercent >= 15) healthScore += 20;
    else if (savingsPercent >= 10) healthScore += 15;
    else if (savingsPercent >= 5) healthScore += 10;
    
    // Needs penalty (if over 50%)
    if (needsPercent <= 50) healthScore += 15;
    else if (needsPercent <= 60) healthScore += 10;
    else if (needsPercent <= 70) healthScore += 5;
    else healthScore -= 10;
    
    // Emergency fund bonus (max +10)
    if (emergencyMonths >= 6) healthScore += 10;
    else if (emergencyMonths >= 3) healthScore += 7;
    else if (emergencyMonths >= 1) healthScore += 3;
    
    // Surplus bonus
    if (surplus >= 0) healthScore += 5;
    else healthScore -= 15;
    
    // Cap score
    healthScore = Math.max(0, Math.min(100, healthScore));

    return {
      totalMonthlyIncome,
      monthlyMainSalary,
      monthlyPartnerSalary,
      monthlyBonus,
      totalEssentials,
      totalDiscretionary,
      totalDebts,
      totalSavings,
      totalExpenses,
      surplus,
      needsPercent,
      wantsPercent,
      savingsPercent,
      debtPercent,
      emergencyMonths,
      healthScore: Math.round(healthScore),
    };
  }, [payFrequency, netSalary, partnerSalary, annualBonus, essentials, discretionary, debts, savings]);

  // Chart data for pie
  const pieData = [
    { name: "Needs", value: calculations.totalEssentials, percent: calculations.needsPercent.toFixed(0), color: COLORS.needs },
    { name: "Wants", value: calculations.totalDiscretionary, percent: calculations.wantsPercent.toFixed(0), color: COLORS.wants },
    { name: "Savings", value: calculations.totalSavings, percent: calculations.savingsPercent.toFixed(0), color: COLORS.savings },
    { name: "Debt", value: calculations.totalDebts, percent: calculations.debtPercent.toFixed(0), color: COLORS.debt },
  ].filter(d => d.value > 0);

  // Bar chart data for 50/30/20 comparison
  const barData = [
    { name: "Needs", actual: calculations.needsPercent, target: 50, fill: COLORS.needs },
    { name: "Wants", actual: calculations.wantsPercent, target: 30, fill: COLORS.wants },
    { name: "Savings", actual: calculations.savingsPercent, target: 20, fill: COLORS.savings },
  ];

  // Export to Excel
  const exportToExcel = async () => {
    const workbook = new ExcelJS.Workbook();
    workbook.creator = "Kara Finance";
    workbook.created = new Date();

    const sheet = workbook.addWorksheet("Budget", {
      properties: { tabColor: { argb: "10B981" } },
    });

    // Column widths
    sheet.columns = [
      { width: 30 },
      { width: 18 },
      { width: 18 },
      { width: 18 },
    ];

    // Styles
    const headerStyle = {
      font: { bold: true, size: 14, color: { argb: "FFFFFF" } },
      fill: { type: "pattern", pattern: "solid", fgColor: { argb: "10B981" } },
      alignment: { horizontal: "center", vertical: "middle" },
      border: {
        top: { style: "thin", color: { argb: "0F766E" } },
        bottom: { style: "thin", color: { argb: "0F766E" } },
      },
    };

    const sectionStyle = {
      font: { bold: true, size: 12, color: { argb: "FFFFFF" } },
      fill: { type: "pattern", pattern: "solid", fgColor: { argb: "1E293B" } },
      alignment: { horizontal: "left", vertical: "middle" },
    };

    const currencyFormat = "£#,##0.00";

    // Title
    sheet.mergeCells("A1:D1");
    const titleRow = sheet.getRow(1);
    titleRow.height = 40;
    titleRow.getCell(1).value = "KARA FINANCE - MONTHLY BUDGET";
    titleRow.getCell(1).style = {
      font: { bold: true, size: 18, color: { argb: "FFFFFF" } },
      fill: { type: "pattern", pattern: "solid", fgColor: { argb: "10B981" } },
      alignment: { horizontal: "center", vertical: "middle" },
    };

    // Date
    sheet.mergeCells("A2:D2");
    const dateRow = sheet.getRow(2);
    dateRow.getCell(1).value = `Generated: ${new Date().toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}`;
    dateRow.getCell(1).style = {
      font: { italic: true, size: 10, color: { argb: "64748B" } },
      alignment: { horizontal: "center" },
    };

    // Empty row
    sheet.addRow([]);

    // ===== INCOME SECTION =====
    const incomeHeader = sheet.addRow(["INCOME", "Monthly Amount", "", ""]);
    incomeHeader.eachCell((cell) => { cell.style = sectionStyle; });
    sheet.mergeCells(`A${incomeHeader.number}:D${incomeHeader.number}`);

    const incomeItems = [
      ["Net Salary", calculations.monthlyMainSalary],
      ["Partner Salary", calculations.monthlyPartnerSalary],
      ["Annual Bonus (Monthly)", calculations.monthlyBonus],
    ];
    
    incomeItems.forEach(([label, value]) => {
      const row = sheet.addRow([label, value]);
      row.getCell(2).numFmt = currencyFormat;
      row.getCell(2).alignment = { horizontal: "right" };
    });

    const incomeTotalRow = sheet.addRow(["Total Monthly Income", calculations.totalMonthlyIncome]);
    incomeTotalRow.getCell(1).style = { font: { bold: true } };
    incomeTotalRow.getCell(2).numFmt = currencyFormat;
    incomeTotalRow.getCell(2).style = { font: { bold: true, color: { argb: "10B981" } }, alignment: { horizontal: "right" } };

    sheet.addRow([]);

    // ===== ESSENTIAL EXPENSES =====
    const essentialsHeader = sheet.addRow(["ESSENTIAL EXPENSES (NEEDS)", "Monthly Amount", "", ""]);
    essentialsHeader.eachCell((cell) => { cell.style = sectionStyle; });
    sheet.mergeCells(`A${essentialsHeader.number}:D${essentialsHeader.number}`);

    ESSENTIAL_EXPENSES.forEach(({ key, label }) => {
      const row = sheet.addRow([label, essentials[key]]);
      row.getCell(2).numFmt = currencyFormat;
      row.getCell(2).alignment = { horizontal: "right" };
    });

    const essentialsTotalRow = sheet.addRow(["Total Essentials", calculations.totalEssentials]);
    essentialsTotalRow.getCell(1).style = { font: { bold: true } };
    essentialsTotalRow.getCell(2).numFmt = currencyFormat;
    essentialsTotalRow.getCell(2).style = { font: { bold: true, color: { argb: "0EA5E9" } }, alignment: { horizontal: "right" } };

    sheet.addRow([]);

    // ===== DISCRETIONARY EXPENSES =====
    const discretionaryHeader = sheet.addRow(["DISCRETIONARY SPENDING (WANTS)", "Monthly Amount", "", ""]);
    discretionaryHeader.eachCell((cell) => { cell.style = sectionStyle; });
    sheet.mergeCells(`A${discretionaryHeader.number}:D${discretionaryHeader.number}`);

    DISCRETIONARY_EXPENSES.forEach(({ key, label }) => {
      const row = sheet.addRow([label, discretionary[key]]);
      row.getCell(2).numFmt = currencyFormat;
      row.getCell(2).alignment = { horizontal: "right" };
    });

    const discretionaryTotalRow = sheet.addRow(["Total Discretionary", calculations.totalDiscretionary]);
    discretionaryTotalRow.getCell(1).style = { font: { bold: true } };
    discretionaryTotalRow.getCell(2).numFmt = currencyFormat;
    discretionaryTotalRow.getCell(2).style = { font: { bold: true, color: { argb: "A855F7" } }, alignment: { horizontal: "right" } };

    sheet.addRow([]);

    // ===== DEBTS =====
    const debtsHeader = sheet.addRow(["DEBT PAYMENTS", "Monthly Amount", "", ""]);
    debtsHeader.eachCell((cell) => { cell.style = sectionStyle; });
    sheet.mergeCells(`A${debtsHeader.number}:D${debtsHeader.number}`);

    const debtItems = [
      ["Credit Cards", debts.creditCards],
      ["Personal Loans", debts.loans],
      ["Student Loan", debts.studentLoan],
      ["Other Debts", debts.other],
    ];
    
    debtItems.forEach(([label, value]) => {
      const row = sheet.addRow([label, value]);
      row.getCell(2).numFmt = currencyFormat;
      row.getCell(2).alignment = { horizontal: "right" };
    });

    const debtsTotalRow = sheet.addRow(["Total Debt Payments", calculations.totalDebts]);
    debtsTotalRow.getCell(1).style = { font: { bold: true } };
    debtsTotalRow.getCell(2).numFmt = currencyFormat;
    debtsTotalRow.getCell(2).style = { font: { bold: true, color: { argb: "F97316" } }, alignment: { horizontal: "right" } };

    sheet.addRow([]);

    // ===== SAVINGS =====
    const savingsHeader = sheet.addRow(["SAVINGS & INVESTMENTS", "Monthly Amount", "", ""]);
    savingsHeader.eachCell((cell) => { cell.style = sectionStyle; });
    sheet.mergeCells(`A${savingsHeader.number}:D${savingsHeader.number}`);

    const savingsItems = [
      ["Monthly Savings", savings.monthlyContribution],
      ["Pension Top-Up", savings.pensionTopUp],
      ["Investments", savings.investments],
    ];
    
    savingsItems.forEach(([label, value]) => {
      const row = sheet.addRow([label, value]);
      row.getCell(2).numFmt = currencyFormat;
      row.getCell(2).alignment = { horizontal: "right" };
    });

    const savingsTotalRow = sheet.addRow(["Total Savings", calculations.totalSavings]);
    savingsTotalRow.getCell(1).style = { font: { bold: true } };
    savingsTotalRow.getCell(2).numFmt = currencyFormat;
    savingsTotalRow.getCell(2).style = { font: { bold: true, color: { argb: "10B981" } }, alignment: { horizontal: "right" } };

    sheet.addRow([]);

    // ===== SUMMARY =====
    const summaryHeader = sheet.addRow(["BUDGET SUMMARY", "Amount", "% of Income", ""]);
    summaryHeader.eachCell((cell) => { cell.style = headerStyle; });

    const summaryItems = [
      ["Total Income", calculations.totalMonthlyIncome, "100%"],
      ["Total Expenses", calculations.totalExpenses, `${((calculations.totalExpenses / calculations.totalMonthlyIncome) * 100).toFixed(1)}%`],
      ["Monthly Surplus/Deficit", calculations.surplus, `${((calculations.surplus / calculations.totalMonthlyIncome) * 100).toFixed(1)}%`],
    ];

    summaryItems.forEach(([label, value, percent]) => {
      const row = sheet.addRow([label, value, percent]);
      row.getCell(2).numFmt = currencyFormat;
      row.getCell(2).alignment = { horizontal: "right" };
      row.getCell(3).alignment = { horizontal: "right" };
      if (label.includes("Surplus")) {
        row.getCell(2).style = { 
          font: { bold: true, color: { argb: value >= 0 ? "10B981" : "EF4444" } },
          alignment: { horizontal: "right" }
        };
      }
    });

    sheet.addRow([]);

    // ===== 50/30/20 ANALYSIS =====
    const analysisHeader = sheet.addRow(["50/30/20 RULE ANALYSIS", "Your %", "Target %", "Status"]);
    analysisHeader.eachCell((cell) => { cell.style = headerStyle; });

    const analysisItems = [
      ["Needs (Essential)", `${calculations.needsPercent.toFixed(1)}%`, "≤50%", calculations.needsPercent <= 50 ? "✓ On Track" : "⚠ Over"],
      ["Wants (Discretionary)", `${calculations.wantsPercent.toFixed(1)}%`, "≤30%", calculations.wantsPercent <= 30 ? "✓ On Track" : "⚠ Over"],
      ["Savings", `${calculations.savingsPercent.toFixed(1)}%`, "≥20%", calculations.savingsPercent >= 20 ? "✓ On Track" : "⚠ Under"],
    ];

    analysisItems.forEach(([label, actual, target, status]) => {
      const row = sheet.addRow([label, actual, target, status]);
      row.getCell(2).alignment = { horizontal: "right" };
      row.getCell(3).alignment = { horizontal: "right" };
      row.getCell(4).alignment = { horizontal: "center" };
      row.getCell(4).style = { 
        font: { color: { argb: status.includes("✓") ? "10B981" : "F59E0B" } },
        alignment: { horizontal: "center" }
      };
    });

    sheet.addRow([]);

    // ===== EMERGENCY FUND =====
    const efHeader = sheet.addRow(["EMERGENCY FUND STATUS", "", "", ""]);
    efHeader.eachCell((cell) => { cell.style = sectionStyle; });
    sheet.mergeCells(`A${efHeader.number}:D${efHeader.number}`);

    const efItems = [
      ["Current Emergency Fund", fmtDecimal(savings.emergencyFund)],
      ["Monthly Essential Costs", fmtDecimal(calculations.totalEssentials)],
      ["Months Covered", `${calculations.emergencyMonths.toFixed(1)} months`],
      ["Target", "3-6 months"],
    ];

    efItems.forEach(([label, value]) => {
      const row = sheet.addRow([label, value]);
      row.getCell(2).alignment = { horizontal: "right" };
    });

    sheet.addRow([]);

    // Footer
    const footerRow = sheet.addRow(["Budget calculated by Kara Finance - karafinance.co.uk"]);
    sheet.mergeCells(`A${footerRow.number}:D${footerRow.number}`);
    footerRow.getCell(1).style = {
      font: { italic: true, size: 9, color: { argb: "64748B" } },
      alignment: { horizontal: "center" },
    };

    // Generate and download
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
    saveAs(blob, `kara-finance-budget-${new Date().toISOString().split("T")[0]}.xlsx`);
  };

  return (
    <div className="bg-slate-950 min-h-screen pt-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        {/* Back */}
        <Link
          to="/tools"
          data-testid="back-to-tools"
          className="inline-flex items-center gap-2 text-slate-400 hover:text-white text-sm mb-8 transition-colors group"
        >
          <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
          Back to Tools
        </Link>

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-xl bg-violet-500/10 flex items-center justify-center">
                <PieChart className="w-5 h-5 text-violet-400" />
              </div>
              <span className="text-violet-400 text-sm font-medium">Budgeting</span>
            </div>
            <h1
              data-testid="budget-calculator-title"
              className="font-outfit text-3xl sm:text-4xl font-bold text-white mb-2 tracking-tight"
            >
              Comprehensive Budget Calculator
            </h1>
            <p className="text-slate-400 max-w-2xl">
              Track your income, expenses, savings, and debts all in one place. See how your budget compares to the 50/30/20 rule.
            </p>
          </div>
          
          <button
            onClick={exportToExcel}
            data-testid="export-excel-btn"
            className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-500 text-white font-semibold px-5 py-3 rounded-xl transition-colors shadow-lg shadow-emerald-500/20"
          >
            <Download className="w-5 h-5" />
            Export to Excel
          </button>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-12 gap-6">
          {/* Left Column - Inputs */}
          <div className="xl:col-span-5 space-y-6">
            {/* Income Section */}
            <div className="bg-slate-900/50 border border-slate-800 rounded-2xl p-6">
              <SectionHeader 
                icon={Wallet} 
                title="Income" 
                subtitle="Your take-home pay after tax, NI, and pension"
                color="text-green-400"
              />
              
              {/* Pay Frequency */}
              <div className="mb-4">
                <label className="text-slate-400 text-sm font-medium mb-2 block">Pay Frequency</label>
                <div className="grid grid-cols-4 gap-2">
                  {Object.entries(PAY_FREQUENCY).map(([key, { label }]) => (
                    <button
                      key={key}
                      data-testid={`pay-freq-${key}`}
                      onClick={() => setPayFrequency(key)}
                      className={`px-3 py-2 text-sm rounded-lg font-medium transition-colors ${
                        payFrequency === key
                          ? "bg-emerald-600 text-white"
                          : "bg-slate-800 text-slate-400 hover:bg-slate-700"
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <InputField
                  label="Net Salary (take-home)"
                  value={netSalary}
                  onChange={setNetSalary}
                  placeholder="2500"
                />
                <InputField
                  label="Partner Salary (optional)"
                  value={partnerSalary}
                  onChange={setPartnerSalary}
                  placeholder="0"
                />
              </div>
              <div className="mt-4">
                <InputField
                  label="Annual Bonus (optional)"
                  value={annualBonus}
                  onChange={setAnnualBonus}
                  placeholder="0"
                />
              </div>
              
              <div className="mt-4 p-3 bg-green-500/10 border border-green-500/20 rounded-lg">
                <p className="text-green-400 text-sm font-medium">
                  Monthly Income: {fmt(calculations.totalMonthlyIncome)}
                </p>
              </div>
            </div>

            {/* Essential Expenses */}
            <div className="bg-slate-900/50 border border-slate-800 rounded-2xl p-6">
              <SectionHeader 
                icon={Home} 
                title="Essential Expenses (Needs)" 
                subtitle="Bills for essential living costs"
                color="text-sky-400"
              />
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {ESSENTIAL_EXPENSES.map(({ key, label, icon, placeholder }) => (
                  <InputField
                    key={key}
                    label={label}
                    icon={icon}
                    value={essentials[key]}
                    onChange={(v) => setEssentials((prev) => ({ ...prev, [key]: v }))}
                    placeholder={placeholder}
                  />
                ))}
              </div>
              
              <div className="mt-4 p-3 bg-sky-500/10 border border-sky-500/20 rounded-lg">
                <p className="text-sky-400 text-sm font-medium">
                  Total Essentials: {fmt(calculations.totalEssentials)} ({calculations.needsPercent.toFixed(1)}% of income)
                </p>
              </div>
            </div>

            {/* Discretionary Expenses */}
            <div className="bg-slate-900/50 border border-slate-800 rounded-2xl p-6">
              <SectionHeader 
                icon={ShoppingBag} 
                title="Discretionary Spending (Wants)" 
                subtitle="Non-essential spending on lifestyle"
                color="text-violet-400"
              />
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {DISCRETIONARY_EXPENSES.map(({ key, label, icon, placeholder }) => (
                  <InputField
                    key={key}
                    label={label}
                    icon={icon}
                    value={discretionary[key]}
                    onChange={(v) => setDiscretionary((prev) => ({ ...prev, [key]: v }))}
                    placeholder={placeholder}
                  />
                ))}
              </div>
              
              <div className="mt-4 p-3 bg-violet-500/10 border border-violet-500/20 rounded-lg">
                <p className="text-violet-400 text-sm font-medium">
                  Total Wants: {fmt(calculations.totalDiscretionary)} ({calculations.wantsPercent.toFixed(1)}% of income)
                </p>
              </div>
            </div>

            {/* Debts */}
            <div className="bg-slate-900/50 border border-slate-800 rounded-2xl p-6">
              <SectionHeader 
                icon={CreditCard} 
                title="Debts & Loans" 
                subtitle="Monthly debt payments"
                color="text-orange-400"
              />
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <InputField
                  label="Credit Cards"
                  icon={CreditCard}
                  value={debts.creditCards}
                  onChange={(v) => setDebts((prev) => ({ ...prev, creditCards: v }))}
                  placeholder="0"
                />
                <InputField
                  label="Personal Loans"
                  value={debts.loans}
                  onChange={(v) => setDebts((prev) => ({ ...prev, loans: v }))}
                  placeholder="0"
                />
                <InputField
                  label="Student Loan"
                  value={debts.studentLoan}
                  onChange={(v) => setDebts((prev) => ({ ...prev, studentLoan: v }))}
                  placeholder="0"
                />
                <InputField
                  label="Other Debts"
                  value={debts.other}
                  onChange={(v) => setDebts((prev) => ({ ...prev, other: v }))}
                  placeholder="0"
                />
              </div>
              
              {calculations.totalDebts > 0 && (
                <div className="mt-4 p-3 bg-orange-500/10 border border-orange-500/20 rounded-lg">
                  <p className="text-orange-400 text-sm font-medium">
                    Total Debt Payments: {fmt(calculations.totalDebts)} ({calculations.debtPercent.toFixed(1)}% of income)
                  </p>
                </div>
              )}
            </div>

            {/* Savings */}
            <div className="bg-slate-900/50 border border-slate-800 rounded-2xl p-6">
              <SectionHeader 
                icon={PiggyBank} 
                title="Savings & Goals" 
                subtitle="Building your financial future"
                color="text-emerald-400"
              />
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <InputField
                  label="Current Emergency Fund"
                  icon={Shield}
                  value={savings.emergencyFund}
                  onChange={(v) => setSavings((prev) => ({ ...prev, emergencyFund: v }))}
                  placeholder="500"
                />
                <InputField
                  label="Monthly Savings"
                  icon={PiggyBank}
                  value={savings.monthlyContribution}
                  onChange={(v) => setSavings((prev) => ({ ...prev, monthlyContribution: v }))}
                  placeholder="200"
                />
                <InputField
                  label="Pension Top-Up"
                  value={savings.pensionTopUp}
                  onChange={(v) => setSavings((prev) => ({ ...prev, pensionTopUp: v }))}
                  placeholder="0"
                />
                <InputField
                  label="Investments (ISA etc)"
                  value={savings.investments}
                  onChange={(v) => setSavings((prev) => ({ ...prev, investments: v }))}
                  placeholder="0"
                />
              </div>
              
              <div className="mt-4 p-3 bg-emerald-500/10 border border-emerald-500/20 rounded-lg">
                <p className="text-emerald-400 text-sm font-medium">
                  Total Monthly Savings: {fmt(calculations.totalSavings)} ({calculations.savingsPercent.toFixed(1)}% of income)
                </p>
              </div>
            </div>
          </div>

          {/* Right Column - Results Dashboard */}
          <div className="xl:col-span-7 space-y-6">
            {/* Summary Cards */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div className="bg-slate-900/50 border border-slate-800 rounded-2xl p-4 text-center">
                <p className="text-slate-400 text-xs font-medium mb-1">Monthly Income</p>
                <p className="text-white text-xl font-bold">{fmt(calculations.totalMonthlyIncome)}</p>
              </div>
              <div className="bg-slate-900/50 border border-slate-800 rounded-2xl p-4 text-center">
                <p className="text-slate-400 text-xs font-medium mb-1">Total Expenses</p>
                <p className="text-white text-xl font-bold">{fmt(calculations.totalExpenses)}</p>
              </div>
              <div className="bg-slate-900/50 border border-slate-800 rounded-2xl p-4 text-center">
                <p className="text-slate-400 text-xs font-medium mb-1">Savings Rate</p>
                <p className={`text-xl font-bold ${calculations.savingsPercent >= 20 ? "text-emerald-400" : "text-amber-400"}`}>
                  {calculations.savingsPercent.toFixed(1)}%
                </p>
              </div>
              <div className="bg-slate-900/50 border border-slate-800 rounded-2xl p-4 text-center">
                <p className="text-slate-400 text-xs font-medium mb-1">Emergency Fund</p>
                <p className={`text-xl font-bold ${calculations.emergencyMonths >= 3 ? "text-emerald-400" : "text-amber-400"}`}>
                  {calculations.emergencyMonths.toFixed(1)} mo
                </p>
              </div>
            </div>

            {/* Health Score & Surplus */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="bg-slate-900/50 border border-slate-800 rounded-2xl p-6">
                <h3 className="font-outfit font-semibold text-white text-sm mb-4 text-center">Budget Health Score</h3>
                <HealthScore score={calculations.healthScore} />
                <p className="text-slate-500 text-xs text-center mt-3">
                  Based on savings rate, spending allocation & emergency fund
                </p>
              </div>
              
              <div className={`rounded-2xl p-6 ${calculations.surplus >= 0 ? "bg-emerald-500/10 border border-emerald-500/20" : "bg-red-500/10 border border-red-500/20"}`}>
                <h3 className="font-outfit font-semibold text-white text-sm mb-4 text-center">Monthly Balance</h3>
                <div className="text-center">
                  <div className={`text-4xl font-bold ${calculations.surplus >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                    {calculations.surplus >= 0 ? "+" : ""}{fmt(calculations.surplus)}
                  </div>
                  <div className="text-slate-500 text-sm mt-1">
                    {calculations.surplus >= 0 ? "Surplus" : "Deficit"}
                  </div>
                </div>
                {calculations.surplus < 0 && (
                  <p className="text-red-400 text-xs text-center mt-3 flex items-center justify-center gap-1">
                    <AlertTriangle className="w-3 h-3" />
                    Review your expenses to balance your budget
                  </p>
                )}
                {calculations.surplus > 0 && (
                  <p className="text-emerald-400 text-xs text-center mt-3 flex items-center justify-center gap-1">
                    <CheckCircle className="w-3 h-3" />
                    Consider adding surplus to savings!
                  </p>
                )}
              </div>
            </div>

            {/* Expense Breakdown Pie Chart */}
            <div className="bg-slate-900/50 border border-slate-800 rounded-2xl p-6">
              <h3 className="font-outfit font-semibold text-white text-lg mb-4">Expense Breakdown</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <RechartsPieChart>
                      <Pie
                        data={pieData}
                        cx="50%"
                        cy="50%"
                        innerRadius={50}
                        outerRadius={90}
                        dataKey="value"
                        stroke="none"
                        label={({ name, percent }) => `${name} ${percent}%`}
                        labelLine={false}
                      >
                        {pieData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip content={<CustomTooltip />} />
                    </RechartsPieChart>
                  </ResponsiveContainer>
                </div>
                <div className="flex flex-col justify-center space-y-3">
                  {pieData.map((item) => (
                    <div key={item.name} className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }} />
                        <span className="text-slate-300 text-sm">{item.name}</span>
                      </div>
                      <span className="text-white font-semibold">{fmt(item.value)}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* 50/30/20 Comparison */}
            <div className="bg-slate-900/50 border border-slate-800 rounded-2xl p-6">
              <h3 className="font-outfit font-semibold text-white text-lg mb-4">50/30/20 Rule Check</h3>
              <div className="space-y-4">
                {barData.map((item) => {
                  const isOver = item.name === "Savings" 
                    ? item.actual < item.target 
                    : item.actual > item.target;
                  return (
                    <div key={item.name} className="space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-slate-300 text-sm font-medium">{item.name}</span>
                        <div className="flex items-center gap-2">
                          <span className="text-white font-semibold">{item.actual.toFixed(1)}%</span>
                          <span className="text-slate-500 text-sm">
                            (target {item.name === "Savings" ? "≥" : "≤"}{item.target}%)
                          </span>
                          {isOver ? (
                            <AlertTriangle className="w-4 h-4 text-amber-400" />
                          ) : (
                            <CheckCircle className="w-4 h-4 text-emerald-400" />
                          )}
                        </div>
                      </div>
                      <div className="relative h-4 bg-slate-800 rounded-full overflow-hidden">
                        <div 
                          className="absolute top-0 bottom-0 w-0.5 bg-white/50 z-10"
                          style={{ left: `${Math.min(item.target, 100)}%` }}
                        />
                        <div
                          className="h-full rounded-full transition-all duration-500"
                          style={{
                            width: `${Math.min(item.actual, 100)}%`,
                            backgroundColor: isOver ? "#f59e0b" : item.fill,
                          }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Emergency Fund Status */}
            <div className="bg-slate-900/50 border border-slate-800 rounded-2xl p-6">
              <h3 className="font-outfit font-semibold text-white text-lg mb-4">Emergency Fund Status</h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
                <div className="text-center p-3 bg-slate-800/50 rounded-xl">
                  <p className="text-slate-400 text-xs mb-1">Current Fund</p>
                  <p className="text-white font-bold text-lg">{fmt(savings.emergencyFund)}</p>
                </div>
                <div className="text-center p-3 bg-slate-800/50 rounded-xl">
                  <p className="text-slate-400 text-xs mb-1">Months Covered</p>
                  <p className={`font-bold text-lg ${calculations.emergencyMonths >= 3 ? "text-emerald-400" : "text-amber-400"}`}>
                    {calculations.emergencyMonths.toFixed(1)}
                  </p>
                </div>
                <div className="text-center p-3 bg-slate-800/50 rounded-xl">
                  <p className="text-slate-400 text-xs mb-1">Target</p>
                  <p className="text-slate-300 font-bold text-lg">3-6 months</p>
                </div>
              </div>
              
              {/* Progress to 3 months */}
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-400">Progress to 3 months</span>
                  <span className="text-slate-300">{Math.min((calculations.emergencyMonths / 3) * 100, 100).toFixed(0)}%</span>
                </div>
                <div className="h-3 bg-slate-800 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-emerald-500 rounded-full transition-all duration-500"
                    style={{ width: `${Math.min((calculations.emergencyMonths / 3) * 100, 100)}%` }}
                  />
                </div>
              </div>
              
              {calculations.emergencyMonths < 3 && (
                <div className="mt-4 p-3 bg-amber-500/10 border border-amber-500/20 rounded-lg">
                  <p className="text-amber-400 text-sm">
                    <strong>Action needed:</strong> Save an additional {fmt((3 * calculations.totalEssentials) - savings.emergencyFund)} to reach 3 months of coverage.
                  </p>
                </div>
              )}
            </div>

            {/* Summary Tip */}
            <div className="bg-emerald-500/5 border border-emerald-500/20 rounded-2xl p-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-emerald-500/10 flex items-center justify-center flex-shrink-0">
                  <TrendingUp className="w-6 h-6 text-emerald-400" />
                </div>
                <div>
                  <h3 className="font-outfit font-semibold text-white text-lg mb-1">
                    {calculations.healthScore >= 70
                      ? "Your budget is looking healthy!"
                      : calculations.healthScore >= 50
                      ? "Your budget is on the right track"
                      : "Let's improve your budget health"}
                  </h3>
                  <p className="text-slate-400 text-sm">
                    {calculations.savingsPercent >= 20
                      ? `Great job! You're saving ${calculations.savingsPercent.toFixed(0)}% of your income.`
                      : `Try to boost your savings rate to at least 20%. You're currently at ${calculations.savingsPercent.toFixed(0)}%.`
                    }
                    {calculations.emergencyMonths < 3 && " Building your emergency fund should be a priority."}
                  </p>
                </div>
              </div>
            </div>

            {/* Info Box */}
            <div className="flex items-start gap-2 bg-slate-900/30 border border-slate-800/50 rounded-xl p-4">
              <Info className="w-4 h-4 text-slate-500 flex-shrink-0 mt-0.5" />
              <p className="text-slate-500 text-xs leading-relaxed">
                The 50/30/20 rule is a guideline, not a strict rule. Adjust based on your circumstances — 
                those in high cost-of-living areas may need more for needs, while aggressive savers might aim for 30%+ savings.
                Export your budget to Excel to keep a record and track changes over time.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
